// Ticket 763/857: voortgangsgrafiek per oefening op het trainingsrapport.
// Herontwerp (857): EEN lijn die alleen het totale trainingsvolume per
// uitgevoerde training binnen het huidige schema toont. Groen wanneer het
// volume t.o.v. de vorige training stijgt of gelijk blijft, rood bij een
// daling; het laatste punt krijgt een label met de actuele waarde. Tik op een
// datapunt om de sets en details van dat trainingsmoment te zien (837).
import { Controller } from "stimulus"
import ApexCharts from 'apexcharts'

export default class extends Controller {
  static values = {
    data: Object
  }
  static targets = ["chart", "details"]

  connect() {
    const data = this.dataValue
    const isKg = data.unit === "kg"
    const unit = isKg ? " kg" : ""
    const points = data.dates.map((date, i) => [new Date(date).getTime(), data.volumes[i]])

    const last = data.volumes[data.volumes.length - 1]
    const prev = data.volumes.length >= 2 ? data.volumes[data.volumes.length - 2] : null
    const declining = prev !== null && last < prev
    const lineColor = declining ? '#EF4444' : '#22C55E'

    const controller = this
    const fmt = v => v.toLocaleString('nl-NL', { maximumFractionDigits: 0 })

    const options = {
      series: [{ name: "Volume", data: points }],
      chart: {
        height: data.height || 200,
        type: 'line',
        background: 'transparent',
        foreColor: '#9CA3AF',
        zoom: { enabled: false },
        toolbar: { show: false },
        animations: { enabled: true, easing: 'easeinout', speed: 600 },
        events: {
          markerClick: function (event, chartContext, { dataPointIndex }) {
            controller.showDetails(dataPointIndex)
          }
        }
      },
      colors: [lineColor],
      stroke: { width: 3, curve: 'straight' },
      markers: {
        size: 4,
        strokeWidth: 2,
        strokeColors: lineColor,
        colors: [lineColor],
        hover: { size: 7 }
      },
      dataLabels: { enabled: false },
      annotations: {
        points: [{
          x: points[points.length - 1][0],
          y: last,
          marker: { size: 5, fillColor: lineColor, strokeColor: '#111827' },
          label: {
            text: fmt(last),
            borderColor: lineColor,
            offsetY: -8,
            style: { background: lineColor, color: '#fff', fontWeight: 700, padding: { left: 6, right: 6, top: 2, bottom: 2 } }
          }
        }]
      },
      grid: {
        borderColor: '#1F2937',
        strokeDashArray: 5,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: true } },
        padding: { top: 8, right: 40, bottom: 0, left: 8 }
      },
      xaxis: {
        type: 'datetime',
        labels: {
          style: { colors: '#6B7280' },
          formatter: function (value, timestamp) {
            return new Date(timestamp).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
          }
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false }
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: {
          style: { colors: '#6B7280' },
          formatter: fmt
        }
      },
      tooltip: {
        theme: 'dark',
        x: {
          formatter: function (value) {
            return new Date(value).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })
          }
        },
        y: {
          formatter: function (val) {
            return val.toLocaleString('nl-NL', { maximumFractionDigits: 1 }) + unit
          }
        }
      }
    }

    const mount = this.hasChartTarget ? this.chartTarget : this.element
    this.chart = new ApexCharts(mount, options)
    this.chart.render()
  }

  // Ticket 837: alle afzonderlijke sets + details van het aangeklikte trainingsmoment
  showDetails(index) {
    if (!this.hasDetailsTarget) return
    const point = (this.dataValue.points || [])[index]
    if (!point) return

    const fmt = v => v.toLocaleString('nl-NL', { maximumFractionDigits: 1 })
    const pctFmt = v => (v > 0 ? "+" : "") + fmt(v) + "%"
    const isKg = this.dataValue.unit === "kg"

    let html = `<div class="font-semibold text-white mb-1">${point.date}</div>`
    if (point.sets && point.sets.length) {
      html += point.sets.map(s => {
        const w = isKg && s.weight > 0 ? (s.reps > 0 ? `${fmt(s.weight)} kg × ${s.reps}` : `${fmt(s.weight)} kg`) : `${s.reps} herhalingen`
        return `<div class="text-gray-300">Set ${s.nr}: ${w}</div>`
      }).join("")
    } else {
      html += `<div class="text-gray-500">Geen ingevulde sets</div>`
    }
    if (point.best) html += `<div class="mt-1 text-gray-200">Beste set: <span class="font-semibold">${point.best}</span></div>`
    if (point.volume !== null && point.volume !== undefined) {
      html += `<div class="text-gray-400">Totaalvolume: ${fmt(point.volume)}${isKg ? " kg" : ""}`
      if (point.volumePct !== null && point.volumePct !== undefined) html += ` (${pctFmt(point.volumePct)})`
      html += `</div>`
    }
    this.detailsTarget.innerHTML = html
    this.detailsTarget.classList.remove("hidden")
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }
}
